import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField
} from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { CompanyAddressDto, CompanyDto, InboundTripDto, InboundTripPAPSDto } from 'dtos'
import { inboundTripPAPSValidationSchema } from 'dtos/inboundTripPAPSDto'
import { Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { AUTOCOMPLETE_PAGESIZE, DEBOUNCE_DELAY, Junction } from 'localConstants'
import { useEffect, useState } from 'react'
import { formatAddress, useDebounce } from 'utils'

interface PAPSManagementAddEditDialogProps {
  initialValues: InboundTripPAPSDto
  inboundTrip: InboundTripDto
  open: boolean
  isLoading: boolean
  onClose: () => void
  onSave: (values: InboundTripPAPSDto) => void
}

export default function PAPSManagementAddEditDialog({
  initialValues,
  inboundTrip,
  isLoading,
  onClose,
  onSave,
  open
}: PAPSManagementAddEditDialogProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingExporterCompanies: false,
    isGettingBuyerCompanies: false,
    isGettingVehicleDestinationCompanies: false,
    isGettingVehicleDestinationCompanyAddresses: false,
    isGettingTitleDestinationCompanies: false,
    isGettingTitleDestinationCompanyAddresses: false
  })

  // 'inboundTripPAPS' is used to pre-populate Buyer Company, Vehicle Destination Company, Vehicle Destination Company Address,
  // Title Destination Company, and Title Destination Company and disable these fields if an Inbound Trip PAPS already exists for
  // 'inboundTrip'.
  // Maxwell Sommer 09/30/2024
  const [inboundTripPAPS, setInboundTripPAPS] = useState<InboundTripPAPSDto | null>(null)

  const [buyerCompanies, setBuyerCompanies] = useState<CompanyDto[]>([])
  const [exporterCompanies, setExporterCompanies] = useState<CompanyDto[]>([])
  const [vehicleDestinationCompanies, setVehicleDestinationCompanies] = useState<CompanyDto[]>([])
  const [vehicleDestinationCompanyAddresses, setVehicleDestinationCompanyAddresses] = useState<CompanyAddressDto[]>([])
  const [titleDestinationCompanies, setTitleDestinationCompanies] = useState<CompanyDto[]>([])
  const [titleDestinationCompanyAddresses, setTitleDestinationCompanyAddresses] = useState<CompanyAddressDto[]>([])

  const getCompaniesAutocompleteBase = (search: string, statuses: string[]) =>
    controllers.getCompanies({
      junction: Junction.AND,
      order: 'asc',
      orderBy: 'name',
      page: 0,
      pageSize: AUTOCOMPLETE_PAGESIZE,
      search,
      statuses
    })

  const getExporterCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingExporterCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isExporterCompany'])
      .then(response => {
        setExporterCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingExporterCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  const getBuyerCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingBuyerCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isBuyerCompany'])
      .then(response => {
        setBuyerCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingBuyerCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  const getVehicleDestinationCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingVehicleDestinationCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isVehicleDestinationCompany'])
      .then(response => {
        setVehicleDestinationCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicleDestinationCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  const getTitleDestinationCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingTitleDestinationCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isTitleDestinationCompany'])
      .then(response => {
        setTitleDestinationCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingTitleDestinationCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (inboundTrip && inboundTrip.inboundTripGuid && open) {
      controllers
        .getInboundTripPAPS(inboundTrip.inboundTripGuid, { page: 0, pageSize: 1, order: 'asc', orderBy: 'createdDateTime' })
        .then(response => {
          if (response.value && response.value.length > 0) {
            setInboundTripPAPS(response.value[0])
          } else {
            setInboundTripPAPS(null)
          }
        })
    }
  }, [inboundTrip, open])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={
          inboundTripPAPS
            ? {
                ...initialValues,
                buyerCompany: inboundTripPAPS.buyerCompany,
                vehicleDestinationCompany: inboundTripPAPS.vehicleDestinationCompany,
                vehicleDestinationCompanyAddress: inboundTripPAPS.vehicleDestinationCompanyAddress,
                titleDestinationCompany: inboundTripPAPS.titleDestinationCompany,
                titleDestinationCompanyAddress: inboundTripPAPS.titleDestinationCompanyAddress
              }
            : initialValues
        }
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={inboundTripPAPSValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>{values.inboundTripPAPSGuid ? 'Edit' : 'Add'} PAPS</DialogTitle>

                  <Grid item xs={12}>
                    <LabelField label='Inbound Trip #' value={inboundTrip.inboundTripNumber} />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting}
                      error={Boolean(touched.papsNumber && errors.papsNumber)}
                      helperText={touched.papsNumber && errors.papsNumber}
                      label='PAPS #'
                      name='papsNumber'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.papsNumber}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      disableListWrap
                      getOptionLabel={exporterCompany => exporterCompany.name!}
                      noOptionsText={
                        loadingState.isGettingExporterCompanies
                          ? 'Searching Exporter Companies...'
                          : 'No Exporter Companies. Start typing to search for Exporter Companies...'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('exporterCompany', value || undefined)
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getExporterCompaniesAutocomplete(value)
                      }}
                      options={exporterCompanies.map(exporterCompany => ({
                        companyGuid: exporterCompany.companyGuid,
                        name: exporterCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.exporterCompany && errors.exporterCompany)}
                          helperText={touched.exporterCompany && errors.exporterCompany}
                          label='Exporter Company'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingExporterCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.exporterCompany
                          ? {
                              companyGuid: values.exporterCompany.companyGuid,
                              name: values.exporterCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || Boolean(inboundTripPAPS)}
                      disableListWrap
                      getOptionLabel={buyerCompany => buyerCompany.name!}
                      noOptionsText={
                        loadingState.isGettingBuyerCompanies
                          ? 'Searching Buyer Companies...'
                          : 'No Buyer Companies. Start typing to search for Buyer Companies...'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('buyerCompany', value || undefined)
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getBuyerCompaniesAutocomplete(value)
                      }}
                      options={buyerCompanies.map(buyerCompany => ({
                        companyGuid: buyerCompany.companyGuid,
                        name: buyerCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.buyerCompany && errors.buyerCompany)}
                          helperText={touched.buyerCompany && errors.buyerCompany}
                          label='Buyer Company'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingBuyerCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.buyerCompany
                          ? {
                              companyGuid: values.buyerCompany.companyGuid,
                              name: values.buyerCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || Boolean(inboundTripPAPS)}
                      disableListWrap
                      getOptionLabel={vehicleDestinationCompany => vehicleDestinationCompany.name!}
                      noOptionsText={
                        loadingState.isGettingVehicleDestinationCompanies
                          ? 'Searching Vehicle Destination Companies...'
                          : 'No Vehicle Destination Companies. Start typing to search for Vehicle Destination Companies...'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('vehicleDestinationCompany', value || undefined)
                        setFieldValue('vehicleDestinationCompanyAddress', undefined)

                        if (value) {
                          setLoadingState('isGettingVehicleDestinationCompanyAddresses', true)
                          controllers
                            .getCompanyAddresses(value?.companyGuid!, {
                              page: 0,
                              pageSize: AUTOCOMPLETE_PAGESIZE,
                              statuses: ['isVehicleDestinationAddress']
                            })
                            .then(response => {
                              setVehicleDestinationCompanyAddresses(response.value)
                            })
                            .finally(() => {
                              setLoadingState('isGettingVehicleDestinationCompanyAddresses', false)
                            })
                        }
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getVehicleDestinationCompaniesAutocomplete(value)
                      }}
                      options={vehicleDestinationCompanies.map(vehicleDestinationCompany => ({
                        companyGuid: vehicleDestinationCompany.companyGuid,
                        name: vehicleDestinationCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.vehicleDestinationCompany && errors.vehicleDestinationCompany)}
                          helperText={touched.vehicleDestinationCompany && errors.vehicleDestinationCompany}
                          label='Vehicle Destination Company'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingVehicleDestinationCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.vehicleDestinationCompany
                          ? {
                              companyGuid: values.vehicleDestinationCompany.companyGuid,
                              name: values.vehicleDestinationCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || Boolean(inboundTripPAPS)}
                      disableListWrap
                      getOptionLabel={address => formatAddress(address)}
                      noOptionsText={
                        loadingState.isGettingVehicleDestinationCompanies
                          ? 'Searching Vehicle Destination Company Addresses...'
                          : 'No Vehicle Destination Company Addresses.'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('vehicleDestinationCompanyAddress', value)
                      }}
                      options={vehicleDestinationCompanyAddresses.map(vehicleDestinationCompanyAddress => ({
                        addressGuid: vehicleDestinationCompanyAddress.addressGuid,
                        addressNickname: vehicleDestinationCompanyAddress.addressNickname,
                        address1: vehicleDestinationCompanyAddress.address1,
                        address2: vehicleDestinationCompanyAddress.address2,
                        city: vehicleDestinationCompanyAddress.city,
                        stateProvince: vehicleDestinationCompanyAddress.stateProvince,
                        postalCode: vehicleDestinationCompanyAddress.postalCode,
                        country: vehicleDestinationCompanyAddress.country
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.vehicleDestinationCompanyAddress && errors.vehicleDestinationCompanyAddress)}
                          helperText={touched.vehicleDestinationCompanyAddress && errors.vehicleDestinationCompanyAddress}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingVehicleDestinationCompanyAddresses && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          label='Vehicle Destination Company Address'
                        />
                      )}
                      value={
                        values.vehicleDestinationCompanyAddress
                          ? {
                              addressGuid: values.vehicleDestinationCompanyAddress.addressGuid,
                              addressNickname: values.vehicleDestinationCompanyAddress.addressNickname,
                              address1: values.vehicleDestinationCompanyAddress.address1,
                              address2: values.vehicleDestinationCompanyAddress.address2,
                              city: values.vehicleDestinationCompanyAddress.city,
                              stateProvince: values.vehicleDestinationCompanyAddress.stateProvince,
                              postalCode: values.vehicleDestinationCompanyAddress.postalCode,
                              country: values.vehicleDestinationCompanyAddress.country
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || Boolean(inboundTripPAPS)}
                      disableListWrap
                      getOptionLabel={titleDestinationCompany => titleDestinationCompany.name!}
                      noOptionsText={
                        loadingState.isGettingTitleDestinationCompanies
                          ? 'Searching Title Destination Companies...'
                          : 'No Title Destination Companies. Start typing to search for Title Destination Companies...'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('titleDestinationCompany', value || undefined)
                        setFieldValue('titleDestinationCompanyAddress', null)

                        if (value) {
                          setLoadingState('isGettingTitleDestinationCompanyAddresses', true)
                          controllers
                            .getCompanyAddresses(value?.companyGuid!, {
                              page: 0,
                              pageSize: AUTOCOMPLETE_PAGESIZE,
                              statuses: ['isTitleDestinationAddress']
                            })
                            .then(response => {
                              setTitleDestinationCompanyAddresses(response.value)
                            })
                            .finally(() => {
                              setLoadingState('isGettingTitleDestinationCompanyAddresses', false)
                            })
                        }
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getTitleDestinationCompaniesAutocomplete(value)
                      }}
                      options={titleDestinationCompanies.map(titleDestinationCompany => ({
                        companyGuid: titleDestinationCompany.companyGuid,
                        name: titleDestinationCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label='Title Destination Company'
                          error={Boolean(touched.titleDestinationCompany && errors.titleDestinationCompany)}
                          helperText={touched.titleDestinationCompany && errors.titleDestinationCompany}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingTitleDestinationCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.titleDestinationCompany
                          ? {
                              companyGuid: values.titleDestinationCompany.companyGuid,
                              name: values.titleDestinationCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || Boolean(inboundTripPAPS)}
                      disableListWrap
                      getOptionLabel={address => formatAddress(address)}
                      noOptionsText={
                        loadingState.isGettingTitleDestinationCompanyAddresses
                          ? 'Searching Title Destination Company Addresses...'
                          : 'No Title Destination Company Addresses.'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('titleDestinationCompanyAddress', value)
                      }}
                      options={titleDestinationCompanyAddresses.map(titleDestinationCompanyAddress => ({
                        addressGuid: titleDestinationCompanyAddress.addressGuid,
                        addressNickname: titleDestinationCompanyAddress.addressNickname,
                        address1: titleDestinationCompanyAddress.address1,
                        address2: titleDestinationCompanyAddress.address2,
                        city: titleDestinationCompanyAddress.city,
                        stateProvince: titleDestinationCompanyAddress.stateProvince,
                        postalCode: titleDestinationCompanyAddress.postalCode,
                        country: titleDestinationCompanyAddress.country
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.titleDestinationCompanyAddress && errors.titleDestinationCompanyAddress)}
                          helperText={touched.titleDestinationCompanyAddress && errors.titleDestinationCompanyAddress}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingTitleDestinationCompanyAddresses && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          label='Title Destination Company Address'
                        />
                      )}
                      value={
                        values.titleDestinationCompanyAddress
                          ? {
                              addressGuid: values.titleDestinationCompanyAddress.addressGuid,
                              addressNickname: values.titleDestinationCompanyAddress.addressNickname,
                              address1: values.titleDestinationCompanyAddress.address1,
                              address2: values.titleDestinationCompanyAddress.address2,
                              city: values.titleDestinationCompanyAddress.city,
                              stateProvince: values.titleDestinationCompanyAddress.stateProvince,
                              postalCode: values.titleDestinationCompanyAddress.postalCode,
                              country: values.titleDestinationCompanyAddress.country
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || When adding or editing a PAPS, all fields are required except for Buyer. || Vehicle Destination Company
                      Address is a cascading dropdown from selected Vehicle Destination Company. || Title Destination Company
                      Address is a cascading dropdown from selected Title Destination Company. || When SAVE is pressed, a check
                      will run to see if there are any other PAPS in the same Inbound Trip and if there are, a check will run to
                      verify that Buyer (if there is one selected), the Vehicle Destination Company, the Vehicle Destination
                      Company Address, the Title Destination Company, and Title Destination Company Address are the same as all
                      other PAPS in that Inbound Trip. || When a user is adding a PAPS to an Inbound Trip and at least one other
                      PAPS already exists for that Inbound Trip, these fields will be auto populated with the same values as the
                      other PAPS within that Inbound Trip and may not be edited: Buyer (if one was selected), Vehicle
                      Destination Company, Vehicle Destination Company Address, Title Destination Company, and Title Destination
                      Company Address. ||
                    </ExplanationAccordion>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button variant='text' color='inherit' onClick={onClose}>
                  Cancel
                </Button>
                <LoadingButton loading={isLoading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
